import { useEffect } from 'react';
import { Icon, type IconName } from '@/components/ui/Icon';
import { useRun } from '@/store/run';

/**
 * Animated Cave Hazard & Dynamic Obstacle Overlay.
 *
 * Triggers when `cave.step()` hits a hazard event:
 *   - bats: flock fluttering across the screen
 *   - falling: debris rocks raining from ceiling
 *   - gas: toxic green fog haze
 *   - lava: heat wave magma flare
 *   - ice: blue frost crystal edges
 *   - quake / dark: screen tremor
 */
const HAZARD_ICON: Record<string, IconName> = {
  bats: 'bat',
  falling: 'rock',
  gas: 'gas',
  lava: 'flame',
  ice: 'ice',
  river: 'wave',
  quake: 'warning',
  dark: 'torch',
};

export function HazardOverlay() {
  const activeHazard = useRun((s) => s.activeHazard);
  const dismiss = useRun((s) => s.dismissHazard);

  useEffect(() => {
    if (!activeHazard) return;

    // Trigger mobile haptic pulse
    try {
      if (typeof window !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate([60, 30, 60]);
      }
    } catch {
      /* non-haptic webkit */
    }

    const timer = setTimeout(() => {
      dismiss();
    }, 3200);

    return () => clearTimeout(timer);
  }, [activeHazard, dismiss]);

  if (!activeHazard) return null;

  const { key, name, message } = activeHazard;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-20 overflow-hidden select-none"
      aria-live="assertive"
      role="alert"
    >
      {/* 1. Hazard Warning Glass Banner */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5 rounded-full border border-amber-500/40 bg-slate-950/80 px-4 py-2 text-xs backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.6)] animate-[bounce_.4s_ease-out]">
        <span className="animate-pulse text-amber-300" aria-hidden>
          <Icon name={HAZARD_ICON[key] ?? 'warning'} size={18} />
        </span>
        <div>
          <p className="font-pixel text-[8px] uppercase tracking-wider text-amber-300 drop-shadow-[0_0_6px_rgba(255,180,60,0.5)]">
            HAZARD · {name}
          </p>
          <p className="text-[10.5px] font-medium text-ink-bright">{message}</p>
        </div>
      </div>

      {/* 2. Bats Flying Swarm Animation */}
      {key === 'bats' && (
        <div className="absolute inset-0 z-20 text-ink-bright">
          <span className="absolute left-[-10%] top-[35%] animate-[batFly_1.8s_ease-in-out_infinite]" style={{ animationDelay: '0s' }}>
            <Icon name="bat" size={28} />
          </span>
          <span className="absolute left-[-15%] top-[25%] animate-[batFly_1.6s_ease-in-out_infinite]" style={{ animationDelay: '0.2s' }}>
            <Icon name="bat" size={22} />
          </span>
          <span className="absolute left-[-20%] top-[45%] animate-[batFly_2.0s_ease-in-out_infinite]" style={{ animationDelay: '0.4s' }}>
            <Icon name="bat" size={28} />
          </span>
          <span className="absolute left-[-12%] top-[55%] animate-[batFly_1.5s_ease-in-out_infinite]" style={{ animationDelay: '0.6s' }}>
            <Icon name="bat" size={18} />
          </span>
        </div>
      )}

      {/* 3. Falling Debris Rock Rain */}
      {key === 'falling' && (
        <div className="absolute inset-0 z-20 text-ink-bright">
          <span className="absolute top-[-10%] left-[20%] animate-[rockRain_1.2s_linear_infinite]" style={{ animationDelay: '0s' }}>
            <Icon name="rock" size={22} />
          </span>
          <span className="absolute top-[-10%] left-[50%] animate-[rockRain_1.4s_linear_infinite]" style={{ animationDelay: '0.25s' }}>
            <Icon name="rock" size={28} />
          </span>
          <span className="absolute top-[-10%] left-[75%] animate-[rockRain_1.1s_linear_infinite]" style={{ animationDelay: '0.5s' }}>
            <Icon name="rock" size={18} />
          </span>
        </div>
      )}

      {/* 4. Toxic Gas Haze Overlay */}
      {key === 'gas' && (
        <div className="absolute inset-0 z-10 bg-emerald-950/25 backdrop-blur-[1px] shadow-[inset_0_0_80px_rgba(34,197,94,0.35)] animate-pulse" />
      )}

      {/* 5. Magma Lava Flare Flash */}
      {key === 'lava' && (
        <div className="absolute inset-0 z-10 bg-orange-950/25 shadow-[inset_0_0_90px_rgba(249,115,22,0.45)] animate-pulse" />
      )}

      {/* 6. Ice Frost Crystal Edge Vignette */}
      {key === 'ice' && (
        <div className="absolute inset-0 z-10 bg-sky-950/20 backdrop-blur-[1px] shadow-[inset_0_0_85px_rgba(56,189,248,0.40)] animate-pulse" />
      )}
    </div>
  );
}
